import { useMatrixDistributionContext, useMatrixSettingsContext } from '@app/components/products/ProductMatrix/context';
import { AddCircleOutline } from '@app/components/various/Icons';
import { Portal } from '@app/components/various/Portal';
import { Scrim } from '@app/components/various/Scrim';
import { Tooltip } from '@app/components/various/Tooltip';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { cn } from '@app/utils/cn';
import { useIsScreenSize } from '@app/utils/hooks';
import { usePopover } from '@app/utils/hooks/usePopover';
import { Preset, Product } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { mergeRefs } from 'react-laag';

import { SizeDistributionContent } from './SizeDistributionContent';
import { SizeDistributionDrawer } from './SizeDistributionDrawer';
import { SizeDistributionPopover } from './SizeDistributionPopover';

const POPOVER_TRIGGER_OFFSET = 32;
const TOOLTIP_HOVER_DELAY = 500;

interface Props {
  variant: Product.Standard;
  deliveryWindowId: string;
  distributions: Preset[];
  disabled?: boolean;
  shouldShowLabel?: boolean;
  isFocusTarget?: boolean;
}

export const SizeDistribution = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const { variant, deliveryWindowId, distributions, disabled, shouldShowLabel = false, isFocusTarget } = props;
  const { isDistributionPopupOpen, distributionVariant, openDistributionPopup, closeDistributionPopup } = useMatrixDistributionContext();
  const { isDesktop } = useMatrixSettingsContext();
  const { t } = useTranslation(['orders']);
  const label = t('orders:size_distribution');
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);
  const contentHeader = t('orders:distribute_sizes_for_item', { item: variant.variantName });

  const shouldRenderSizeDistribution = isDistributionPopupOpen && distributionVariant?.variant === variant.variant;

  const open = React.useCallback(() => {
    openDistributionPopup(variant);
  }, [openDistributionPopup, variant]);

  const close = React.useCallback(() => {
    closeDistributionPopup();
  }, [closeDistributionPopup]);

  const { layerProps, triggerProps, renderLayer } = usePopover({
    escapeHandlerEnabled: shouldRenderSizeDistribution,
    handleDisappear: close,
    layerProps: {
      isOpen: shouldRenderSizeDistribution && !isMobile,
      placement: 'top-center',
      triggerOffset: POPOVER_TRIGGER_OFFSET,
    },
  });

  const sizeDistributionContent = (
    <SizeDistributionContent variant={variant} close={close} distributions={distributions} deliveryWindowId={deliveryWindowId} />
  );

  return (
    <>
      <Tooltip content={!shouldShowLabel && label} hoverDelay={TOOLTIP_HOVER_DELAY}>
        {/* div allows opening popover with keyboard */}
        <div className="h-fit">
          <button
            type="button"
            data-testid={`addDistribution-${variant.variant}`}
            color="secondary"
            className={cn('flex h-full w-full items-center justify-center gap-1 disabled:cursor-not-allowed', {
              'absolute-cursor-pointer disabled:absolute-cursor-not-allowed': isDesktop,
              'focus-visible:!outline-none': !shouldShowLabel,
            })}
            aria-label={!shouldShowLabel ? label : undefined}
            disabled={disabled}
            ref={mergeRefs(ref, triggerProps.ref)}
            data-focus-target={isFocusTarget}
            onClick={open}
          >
            <AddCircleOutline />
            {shouldShowLabel && <span className="body-5">{label}</span>}
          </button>
        </div>
      </Tooltip>
      {isMobile ?
        <>
          <Scrim isVisible={shouldRenderSizeDistribution} onClick={close} />
          <Portal>
            <SizeDistributionDrawer title={contentHeader} isOpen={shouldRenderSizeDistribution} onClose={close}>
              {sizeDistributionContent}
            </SizeDistributionDrawer>
          </Portal>
        </>
      : renderLayer(
          <SizeDistributionPopover isOpen={shouldRenderSizeDistribution} title={contentHeader} onClose={close} {...layerProps}>
            {sizeDistributionContent}
          </SizeDistributionPopover>,
        )
      }
    </>
  );
});
